import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, catchError, expand, map, reduce, throwError, type Observable } from 'rxjs';
import type {
  EntityCapabilities,
  EntityGrantee,
  EntityGrantSet,
  EntityType,
  GranteeSearchResult,
  RecipientAccessMode,
  ReplaceEntityGrants,
  UserEntityAccessRow,
} from '@zag/auth/models';
import { ENTITY_FAMILIES } from '@zag/auth/models';
import { SKIP_FORBIDDEN_TOAST } from '@zag/ui';

export function isFullModeRejection(error: HttpErrorResponse): boolean {
  const body = (error.error ?? {}) as Record<string, unknown>;
  return error.status === 400 && body['errorCode'] === 'ENTITY_GRANT_FULL_MODE';
}

export const ENTITY_ACCESS_MANAGE_PERMISSIONS: Record<EntityType, string | null> = {
  person: ENTITY_FAMILIES.person.ops.manage,
  team: ENTITY_FAMILIES.team.ops.manage,
  department: ENTITY_FAMILIES.department.ops.manage,
  calendar: ENTITY_FAMILIES.calendar.ops.manage,
  location: ENTITY_FAMILIES.location.ops.manage,
  skill: ENTITY_FAMILIES.skill.ops.manage,
  plan: ENTITY_FAMILIES.plan.ops.manage,
};

export interface EntityLabelOption {
  id: string;
  label: string;
}

interface PersonLabelDto {
  id: string;
  firstName: string;
  lastName: string;
}

interface NamedEntityDto {
  id: string;
  name: string;
}

interface EntityListMeta {
  skip: number;
  count: number;
  take: number;
  total: number;
  hasMore: boolean;
}

interface EntityListResponse<T> {
  data: T[];
  meta: EntityListMeta;
}

@Injectable({ providedIn: 'root' })
export class EntityAccessService {
  private readonly http = inject(HttpClient);
  private readonly usersBasePath = '/api/v1/rbac/users';

  basePathFor(entityType: EntityType): string {
    switch (entityType) {
      case 'person':
        return '/api/v1/core/persons';
      case 'team':
        return '/api/v1/planning/teams';
      case 'department':
        return '/api/v1/core/departments';
      case 'calendar':
        return '/api/v1/calendar/calendars';
      case 'location':
        return '/api/v1/core/locations';
      case 'skill':
        return '/api/v1/core/skills';
      case 'plan':
        return '/api/v1/planning/plans';
    }
  }

  managePermissionFor(entityType: EntityType): string | null {
    return ENTITY_FAMILIES[entityType].ops.manage;
  }

  private grantSetUrl(entityType: EntityType, entityId: string, grantee: EntityGrantee): string {
    const base = this.basePathFor(entityType);
    return grantee.kind === 'group' ? `${base}/${entityId}/access/group/${grantee.id}` : `${base}/${entityId}/access/${grantee.id}`;
  }

  // The dialog renders its own revoked/conflict/error states for every call
  // below, so entity-scoped 403s must not also fire the global toast.
  private silentContext(): HttpContext {
    return new HttpContext().set(SKIP_FORBIDDEN_TOAST, true);
  }

  getGrantSet(entityType: EntityType, entityId: string, grantee: EntityGrantee): Observable<EntityGrantSet> {
    return this.http.get<EntityGrantSet>(this.grantSetUrl(entityType, entityId, grantee), { context: this.silentContext() });
  }

  getCapabilities(entityType: EntityType, entityId: string): Observable<EntityCapabilities> {
    return this.http.get<EntityCapabilities>(`${this.basePathFor(entityType)}/${entityId}/access-capabilities`, {
      context: this.silentContext(),
    });
  }

  replaceGrants(
    entityType: EntityType,
    entityId: string,
    grantee: EntityGrantee,
    permissions: string[],
    ifMatch: string
  ): Observable<EntityGrantSet> {
    const body: ReplaceEntityGrants = grantee.kind === 'group' ? { groupId: grantee.id, permissions } : { userId: grantee.id, permissions };
    const headers = new HttpHeaders({ 'If-Match': ifMatch });
    return this.http.put<EntityGrantSet>(this.grantSetUrl(entityType, entityId, grantee), body, {
      headers,
      context: this.silentContext(),
    });
  }

  deleteGrants(entityType: EntityType, entityId: string, grantee: EntityGrantee, ifMatch: string): Observable<void> {
    const headers = new HttpHeaders({ 'If-Match': ifMatch });
    return this.http.delete<void>(this.grantSetUrl(entityType, entityId, grantee), { headers, context: this.silentContext() });
  }

  searchGrantees(entityType: EntityType, entityId: string, query?: string): Observable<GranteeSearchResult> {
    const params = query ? new HttpParams().set('q', query) : undefined;
    return this.http.get<GranteeSearchResult>(`${this.basePathFor(entityType)}/${entityId}/access-grantees`, {
      params,
      context: this.silentContext(),
    });
  }

  listGrantsForEntity(entityType: EntityType, entityId: string): Observable<EntityGrantSet[]> {
    return this.http.get<EntityGrantSet[]>(`${this.basePathFor(entityType)}/${entityId}/access`, {
      context: this.silentContext(),
    });
  }

  listUserAccess(userId: string): Observable<UserEntityAccessRow[]> {
    return this.http.get<UserEntityAccessRow[]>(`${this.usersBasePath}/${userId}/entity-access`, { context: this.silentContext() });
  }

  getRecipientAccess(userId: string): Observable<RecipientAccessMode> {
    return this.http.get<RecipientAccessMode>(`${this.usersBasePath}/${userId}/access-mode`, { context: this.silentContext() });
  }

  labelFor(entityType: EntityType, entityId: string): Observable<string> {
    const context = this.silentContext();
    switch (entityType) {
      case 'person':
        return this.http.get<PersonLabelDto>(`${this.basePathFor(entityType)}/${entityId}`, { context }).pipe(
          map(person => `${person.firstName} ${person.lastName}`),
          catchError(error => throwError(() => this.restrictedOn404(error)))
        );
      case 'team':
      case 'department':
      case 'calendar':
      case 'location':
      case 'skill':
      case 'plan':
        return this.http.get<NamedEntityDto>(`${this.basePathFor(entityType)}/${entityId}`, { context }).pipe(
          map(entity => entity.name),
          catchError(error => throwError(() => this.restrictedOn404(error)))
        );
    }
  }

  searchEntities(entityType: EntityType, query?: string): Observable<EntityLabelOption[]> {
    const term = query?.trim().toLowerCase();
    return this.listOptions(entityType).pipe(
      map(options => (term ? options.filter(option => option.label.toLowerCase().includes(term)) : options))
    );
  }

  private restrictedOn404(error: unknown): unknown {
    return error instanceof HttpErrorResponse && error.status === 404 ? new Error('Restricted') : error;
  }

  private listOptions(entityType: EntityType): Observable<EntityLabelOption[]> {
    switch (entityType) {
      case 'person':
        return this.listSkipTake<PersonLabelDto>(this.basePathFor(entityType)).pipe(
          map(persons => persons.map(person => ({ id: person.id, label: `${person.firstName} ${person.lastName}` })))
        );
      case 'team':
        return this.listSkipTake<NamedEntityDto>(this.basePathFor(entityType), 'name:asc').pipe(
          map(teams => teams.map(team => ({ id: team.id, label: team.name })))
        );
      case 'calendar':
        return this.listCalendarPages().pipe(map(calendars => calendars.map(calendar => ({ id: calendar.id, label: calendar.name }))));
      case 'location':
      case 'department':
      case 'skill':
      case 'plan':
        return this.listSkipTake<NamedEntityDto>(this.basePathFor(entityType), 'name:asc').pipe(
          map(entities => entities.map(entity => ({ id: entity.id, label: entity.name })))
        );
    }
  }

  private listSkipTake<T>(basePath: string, sort?: string): Observable<T[]> {
    const take = 200;
    const context = this.silentContext();
    const loadPage = (skip: number): Observable<EntityListResponse<T>> => {
      let params = new HttpParams().set('skip', String(skip)).set('take', String(take));
      if (sort) params = params.set('sort', sort);
      return this.http.get<EntityListResponse<T>>(basePath, { params, context });
    };
    return loadPage(0).pipe(
      expand(response => (response.meta.hasMore ? loadPage(response.meta.skip + response.meta.count) : EMPTY)),
      map(response => response.data),
      reduce((all, page) => [...all, ...page], [] as T[])
    );
  }

  private listCalendarPages(): Observable<NamedEntityDto[]> {
    const limit = 100;
    const context = this.silentContext();
    const loadPage = (page: number): Observable<EntityListResponse<NamedEntityDto>> => {
      const params = new HttpParams().set('page', String(page)).set('limit', String(limit)).set('sortBy', 'name').set('sortOrder', 'asc');
      return this.http.get<EntityListResponse<NamedEntityDto>>(this.basePathFor('calendar'), { params, context });
    };
    return loadPage(1).pipe(
      expand((response, index) => (response.meta.hasMore ? loadPage(index + 2) : EMPTY)),
      map(response => response.data),
      reduce((all, page) => [...all, ...page], [] as NamedEntityDto[])
    );
  }
}
